import { useCallback, useEffect, useMemo, useState } from "react";

import { useLocation } from "react-router-dom";
import {
  CategoryStatus,
  type Cluster,
  ClusterDiscoveryStatus,
  type ClusterWithCloudConnect,
  type EarlyAccessFeature,
  IntegrationModel,
  type KubernetesFeature,
} from "@doitintl/cmp-models";
import { getCollection, type WithModel } from "@doitintl/models-any";
import { useCollectionData, useDocumentData } from "@doitintl/models-hooks";

import { useApiContext } from "../../api/context";
import { useCloudConnect } from "../../Components/hooks/useCloudConnect";
import { useAuthContext } from "../../Context/AuthContext";
import { useCustomerContext } from "../../Context/CustomerContext";
import { getCachingKeys } from "../../utils/cachingKeys";
import { InsightAccessType } from "../Insights/types";
import { consoleErrorWithSentry, useErrorSnackbar } from "../Integrations/Slack/utils";
import { useAWSAccounts } from "../Settings/AWS/hooks";
import { AccountStatus, AWSFeatureName, FeatureDetails } from "../Settings/AWS/types";
import { useCloudConnectData } from "../Settings/GCP/useCloudConnectData";
import {
  customerHasGKECluster,
  fetchClusterMetrics,
  fetchDashboardClusters,
  fetchDashboardWorkloads,
  fetchWorkloadMetrics,
} from "./api";
import { type ClusterTableItem } from "./Clusters/Table/types";
import { getClusterAgentStatus, getClusterComputedStatus, getClusterEnabledFeatures } from "./Clusters/Table/utils";
import { Percentile } from "./Dashboard/ClusterDetail/Components/PercentileSelector";
import { TimeRange } from "./Dashboard/ClusterDetail/Components/TimeRangeSelector";
import { useClusterDashboardContext } from "./Dashboard/Fsk8sPage/Table/context";
import {
  type DashboardCluster,
  type DashboardClustersResponse,
  type DashboardWorkloadsResponse,
  type K8sUtilizationMetric,
  type K8sWorkloadMetric,
} from "./types";
import {
  clusterHas30dPerfectScaleData,
  findMatchingCloudConnectDoc,
  getEmptyDashboardCluster,
  getFormattedBillingCost,
  getTopTenClustersByCost,
} from "./utils";

export interface UseClusterOptions {
  includeDeletedClusters?: boolean;
}

const defaultClusterOptions: UseClusterOptions = {
  includeDeletedClusters: false,
};

export function useCluster(id: Cluster["id"] | null | undefined) {
  const [clusters, loading] = useClusters();
  const cluster = clusters?.find((c) => c.id === id);
  return [cluster as Cluster | undefined, loading] as const;
}

export function useClusters(options: UseClusterOptions = defaultClusterOptions) {
  const { customer } = useCustomerContext();

  let query = getCollection(IntegrationModel)
    .doc("fsk8s")
    .collection("fsk8sClusters")
    .where("customerRef", "==", customer.ref);

  if (query && !options.includeDeletedClusters) {
    query = query.where("discoveryStatus", "==", ClusterDiscoveryStatus.HEALTHY);
  }

  return useCollectionData(query, {
    caching: true,
    cachingKeys: getCachingKeys(customer?.id),
    idField: "id",
  });
}

export function useClustersWithCloudConnect(options: UseClusterOptions = defaultClusterOptions) {
  const { customer } = useCustomerContext({ allowNull: true });
  const [clusters, loading] = useClusters(options);
  const [cachedClusters, setCachedClusters] = useState<WithModel<ClusterWithCloudConnect>[]>([]);
  const [cloudConnectDocs, cloudConnectLoading] = useCloudConnect(customer);

  const assignCloudConnectDocs = useCallback(async () => {
    if (!clusters) return;

    const enrichedClusters = clusters?.map((cluster: WithModel<ClusterWithCloudConnect>) => ({
      ...cluster,
      cloudConnect: findMatchingCloudConnectDoc(cluster, cloudConnectDocs),
    }));

    setCachedClusters(enrichedClusters);
  }, [clusters, cloudConnectDocs]);

  useEffect(() => {
    assignCloudConnectDocs();
  }, [assignCloudConnectDocs]);

  return [cachedClusters, loading || cloudConnectLoading] as const;
}

export function useKubernetesFeatures() {
  const { customer } = useCustomerContext({ allowNull: true });
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });

  const canCustomerAccessFeature = (feature: KubernetesFeature) => {
    if (isDoitEmployee || !customer || !Array.isArray(customer.earlyAccessFeatures) || !feature.featureFlag) {
      return true;
    }

    return customer.earlyAccessFeatures.includes(feature.featureFlag as EarlyAccessFeature);
  };

  const query = getCollection(IntegrationModel).doc("fsk8s").collection("fsk8sFeatures");

  const [features, loading] = useCollectionData(query, {
    idField: "id",
  });

  const availableFeatures = features?.filter(canCustomerAccessFeature);

  const featureMap: Record<KubernetesFeature["id"], KubernetesFeature> | undefined = availableFeatures?.reduce(
    (acc, feature) => {
      acc[feature?.id] = feature;
      return acc;
    },
    {}
  );

  return [availableFeatures, loading, featureMap] as const;
}

export function useGKEAndCloudConnect() {
  const [hasGKE, setHasGKE] = useState(false);
  const api = useApiContext();
  const { customer } = useCustomerContext();

  const fetchGKECluster = useCallback(async () => {
    const result = await customerHasGKECluster(api, customer.id);
    setHasGKE(Boolean(result.data));
  }, [api, customer.id]);

  useEffect(() => {
    fetchGKECluster();
  }, [fetchGKECluster]);

  const [cloudConnect, isLoading] = useCloudConnect(customer);
  // Whether the customer has any healthy cloud connect doc, in which case we don't show the banner
  const hasCloudConnect =
    cloudConnect?.some((sa) => sa.cloudPlatform === "google-cloud" && sa.categoriesStatus?.["fsk8s-core"] === 1) ||
    false;

  // A list of all the non-healthy cloud connect of the customer
  const nonHealthyCloudConnects = cloudConnect?.filter(
    (sa) => sa.cloudPlatform === "google-cloud" && sa.categoriesStatus?.["fsk8s-core"] !== 1
  );

  // A list of the organization scoped cloud connects with missing fsk8s configs
  const nonHealthyOrganizationCloudConnectsIds = cloudConnect
    ?.filter(
      (sa) =>
        sa.cloudPlatform === "google-cloud" && sa.categoriesStatus?.["fsk8s-core"] !== 1 && sa.scope === "organization"
    )
    .map((sa) => sa.id);

  // Other cloud connects that don't have fsk8s permissions
  const cloudConnectsNeedPermissions = cloudConnect?.filter(
    (sa) =>
      sa.cloudPlatform === "google-cloud" &&
      !(sa.categoriesStatus?.["fsk8s-core"] !== 1 && sa.scope === "organization") &&
      sa.supportedFeatures?.some((feature) => feature.name === "fsk8s-core" && feature.hasRequiredPermissions === false)
  );

  const hasMissingCloudConnectPermissions = cloudConnectsNeedPermissions?.length > 0;

  let accessType: InsightAccessType;
  if (!hasGKE || !hasCloudConnect) {
    // If customer has no GKE clusters or no cloud connect accounts enabled
    accessType = InsightAccessType.No_access;
  } else if (hasGKE && hasCloudConnect && nonHealthyCloudConnects.length === 0) {
    // Customer has GKE clusters and all accounts have proper permissions
    accessType = InsightAccessType.Full;
  } else if (hasGKE && hasCloudConnect && nonHealthyCloudConnects.length > 0) {
    if (
      nonHealthyCloudConnects.length === cloudConnect.length &&
      !nonHealthyCloudConnects.some((sa) => sa.categoriesStatus?.["fsk8s-core"] !== 4) // partial access
    ) {
      accessType = InsightAccessType.No_access;
    } else {
      // Customer has GKE clusters and some but not all accounts have proper permissions
      accessType = InsightAccessType.Partial;
    }
  } else {
    accessType = InsightAccessType.No_access;
  }

  return {
    hasGKE,
    hasCloudConnect,
    hasMissingCloudConnectPermissions,
    nonHealthyOrganizationCloudConnectsIds,
    accessType,
    isLoading,
  };
}

export const useClusterMetrics = (cluster: Cluster | undefined, timeRange: TimeRange, percentile: Percentile) => {
  const api = useApiContext();
  const { customer } = useCustomerContext();
  const errorSnackbar = useErrorSnackbar();

  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<K8sUtilizationMetric[] | null>(null);

  const clusterId = cluster?.id;

  const fetchMetrics = useCallback(async () => {
    if (!clusterId) {
      setMetrics([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetchClusterMetrics(api, customer.id, clusterId, timeRange, percentile);
      const { data } = response.data;
      const formattedMetrics = typeof data === "string" ? JSON.parse(data) : data;
      setMetrics(formattedMetrics);
    } catch (error) {
      consoleErrorWithSentry(error);
      errorSnackbar(`Error fetching cluster metrics`);
    } finally {
      setLoading(false);
    }
  }, [clusterId, customer.id, timeRange, percentile, api, errorSnackbar]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return { metrics: metrics ?? [], loading };
};

interface UseWorkloadsMetricsProps {
  clusterId: string;
  workloadIds: string[];
  timeRange?: TimeRange;
  percentile?: Percentile;
}

export const useWorkloadsMetrics = ({
  clusterId,
  workloadIds,
  timeRange = TimeRange.T_30D,
  percentile = Percentile.P_99_9,
}: UseWorkloadsMetricsProps) => {
  const api = useApiContext();
  const { customer } = useCustomerContext();
  const errorSnackbar = useErrorSnackbar();

  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [workloadMetricsMap, setWorkloadMetricsMap] = useState<Record<string, K8sWorkloadMetric[]>>({});

  const fetchMetricsForWorkloads = useCallback(
    async (workloadsToFetch: string[]) => {
      const newMetricsMap = { ...workloadMetricsMap };
      setLoading(true);

      try {
        const promises = workloadsToFetch.map(async (id) => {
          const response = await fetchWorkloadMetrics({
            api,
            clusterId,
            timeRange,
            percentile,
            workloadId: id,
            customerId: customer.id,
          });

          const { data } = response.data;
          newMetricsMap[id] = data?.toReversed() ?? [];
        });

        await Promise.all(promises);
        setWorkloadMetricsMap(newMetricsMap);
      } catch (error) {
        setHasError(true);
        errorSnackbar("Error fetching workload metrics. Please try again later.");
        consoleErrorWithSentry(error);
      } finally {
        setLoading(false);
      }
    },
    [api, customer.id, clusterId, timeRange, percentile, workloadMetricsMap, errorSnackbar]
  );

  useEffect(() => {
    const workloadsToFetch = workloadIds.filter((id) => !workloadMetricsMap[id]);

    // If we got errors, block any new fetch until the user refreshes the page
    if (workloadsToFetch.length > 0 && !loading && !hasError) {
      fetchMetricsForWorkloads(workloadsToFetch);
    }
  }, [workloadIds, workloadMetricsMap, fetchMetricsForWorkloads, loading, hasError]);

  useEffect(() => {
    setWorkloadMetricsMap({});
  }, [timeRange, percentile]);

  const workloadMetricsMapBySelectedWorkloads = useMemo(
    () => Object.fromEntries(Object.entries(workloadMetricsMap).filter(([w]) => workloadIds.includes(w))),
    [workloadIds, workloadMetricsMap]
  );

  return {
    loading,
    workloads: workloadMetricsMapBySelectedWorkloads,
  };
};

export const useClusterTableItems = () => {
  const [clusters, loading] = useClustersWithCloudConnect({
    includeDeletedClusters: true,
  });
  const [, , featureMap] = useKubernetesFeatures();

  const tableItems: ClusterTableItem[] = useMemo(
    () =>
      clusters?.map((cluster) => {
        const tableItem: ClusterTableItem = {
          ...cluster,
          computedStatus: getClusterComputedStatus(cluster),
          enabledFeatures: getClusterEnabledFeatures(cluster, featureMap),
          agentStatus: getClusterAgentStatus(cluster),
        };

        return tableItem;
      }) || [],
    [clusters, featureMap]
  );

  return [tableItems, loading] as const;
};

export const useDashboardClusters = (timeRange?: TimeRange) => {
  const api = useApiContext();
  const { customer } = useCustomerContext();
  const errorSnackbar = useErrorSnackbar();
  const [clusters, clustersLoading] = useClusterTableItems();

  const [dashboardClusters, setDashboardClusters] = useState<DashboardClustersResponse | undefined>();
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  // get the doitTestCase from the url query params
  const location = useLocation();
  const doitTestCase = new URLSearchParams(location.search).get("doitTestCase");

  const fetchClusters = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchDashboardClusters(api, customer.id, timeRange, doitTestCase);
      setDashboardClusters(response.data);
    } catch (error) {
      errorSnackbar("Error fetching dashboard clusters");
      consoleErrorWithSentry(error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [api, customer.id, errorSnackbar, timeRange, doitTestCase]);

  useEffect(() => {
    fetchClusters();
  }, [fetchClusters]);

  const enrichedDashboardClusters: DashboardCluster[] = useMemo(() => {
    const apiClusters: DashboardCluster[] = [...(dashboardClusters?.clusters || [])];

    // Clusters that have 30d perfect scale data but are not in the selected time range
    const clustersMissingFromCurrentTimeRange = (clusters ?? []).filter(
      (c) => clusterHas30dPerfectScaleData(c) && !apiClusters.some((apiCluster) => apiCluster.name === c.name)
    );

    clustersMissingFromCurrentTimeRange.forEach((missingCluster) => {
      apiClusters.push(getEmptyDashboardCluster(missingCluster));
    });

    return (
      apiClusters?.map((cluster) => {
        const doitCluster = clusters?.find((doitCluster) => doitCluster.name === cluster.name);
        const doitBillingCosts = getFormattedBillingCost(doitCluster);
        const perfectscaleCost = cluster.cost ?? 0;

        const selectedTimeRange = timeRange ?? TimeRange.T_30D;
        const cost = doitBillingCosts[selectedTimeRange]?.cost ?? 0;

        return {
          ...cluster,
          doitCluster,
          doitBillingCosts,
          cost,
          perfectscaleCost,
          hasDataForSelectedTimeRange: cluster.hasDataForSelectedTimeRange ?? true,
        };
      }) ?? []
    );
  }, [clusters, dashboardClusters, timeRange]);

  return {
    initialLoading,
    dashboardClusters: {
      ...dashboardClusters,
      clusters: enrichedDashboardClusters,
    },
    loading: loading || clustersLoading,
  };
};

export const useDashboardWorkloads = (clusterId: string, timeRange?: TimeRange) => {
  const api = useApiContext();
  const { customer } = useCustomerContext();
  const errorSnackbar = useErrorSnackbar();

  const [dashboardWorkloads, setDashboardWorkloads] = useState<DashboardWorkloadsResponse>();
  const [loading, setLoading] = useState(false);

  const fetchWorkloads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchDashboardWorkloads(api, customer.id, clusterId, timeRange);
      setDashboardWorkloads(response.data);
    } catch (error) {
      errorSnackbar("Error fetching dashboard workloads");
      consoleErrorWithSentry(error);
    } finally {
      setLoading(false);
    }
  }, [api, customer.id, errorSnackbar, clusterId, timeRange]);

  useEffect(() => {
    fetchWorkloads();
  }, [fetchWorkloads]);

  return {
    dashboardWorkloads: dashboardWorkloads?.workloads ?? [],
    loading,
  };
};

export const useTopTenClusters = () => {
  const { dashboardClusters, timeRange, loading } = useClusterDashboardContext();

  const topTenClusters = useMemo(
    () => getTopTenClustersByCost(dashboardClusters.clusters, timeRange),
    [dashboardClusters, timeRange]
  );

  return { topTenClusters, loading };
};

export const useCustomerHasFsk8sAWSPermissions = () => {
  const [accounts, _, accountsAreLoading] = useAWSAccounts();

  const healthyAccounts = accounts.filter((a) => a.status === AccountStatus.healthy);
  const customerHasFsk8sAWSPermissions = healthyAccounts.some((a) =>
    a.featureNames.includes(FeatureDetails[AWSFeatureName.fsk8s_core].displayName)
  );

  return {
    customerHasFsk8sAWSPermissions,
    loading: accountsAreLoading,
  };
};

export const useCustomerHasFsk8sGCPPermissions = () => {
  const { customer } = useCustomerContext();
  const [cloudConnectData, , isLoaded] = useCloudConnectData(customer.id);

  const customerHasFsk8sGcpPermissions = cloudConnectData.some(
    (cc) => cc.data.categoriesStatus["fsk8s-core"] === CategoryStatus.Healthy
  );

  return {
    customerHasFsk8sGcpPermissions,
    loading: !isLoaded,
  };
};

export const useCustomerHasFsk8sPermissions = () => {
  const { customerHasFsk8sAWSPermissions, loading: awsLoading } = useCustomerHasFsk8sAWSPermissions();
  const { customerHasFsk8sGcpPermissions, loading: gcpLoading } = useCustomerHasFsk8sGCPPermissions();

  return {
    customerHasFsk8sPermissions: customerHasFsk8sAWSPermissions || customerHasFsk8sGcpPermissions,
    loading: awsLoading || gcpLoading,
  };
};

export const useCustomerHasAgentInstalled = () => {
  const [clusters, loading] = useClusters();

  const hasAgentInstalled = clusters?.some((c) => c.perfectScale?.PsClusterID);

  return {
    customerHasAgentInstalled: hasAgentInstalled,
    loading,
  };
};

export const useLocalStorageState = <T>(initialValue: T, key: string) => {
  const [state, setState] = useState<T>(() => {
    try {
      const storedValue = localStorage.getItem(key);
      return storedValue ? (JSON.parse(storedValue) as T) : initialValue;
    } catch (error) {
      consoleErrorWithSentry(error);
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      consoleErrorWithSentry(error);
    }
  }, [key, state]);

  return [state, setState] as const;
};

export const useFsk8sConfig = () => {
  const doc = getCollection(IntegrationModel).doc("fsk8s").collection("config").doc("global");
  return useDocumentData(doc);
};

export const useCustomerHasPerfectScale = () =>
  // const { customer } = useCustomerContext();
  // return !!customer.perfectscale?.tenantID;

  // TODO: Check properly when we integrate with perfectscale
  false;
